import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, OBLIGATION_STATUS, WORK_ORDER_STATUS } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { fmtDate } from '../lib/fmt';

type Due = { id: string; due_date: string | null; status: string; assets: { name: string } | null; asset_obligation_rules: { name_i18n: Record<string, string>; rule_code: string } | null };
type WO = { id: string; wo_number: string | null; status: string; created_at: string; assets: { name: string } | null; properties: { name: string } | null };

export function Preventive() {
  const { t, i18n } = useTranslation();
  const { id: orgId, timezone } = useOrg();
  const [windowDays, setWindowDays] = useState<number>(30);
  const [due, setDue] = useState<Due[]>([]);
  const [wos, setWos] = useState<WO[]>([]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<number | null>(null);

  const load = useCallback(async () => {
    const o = await supabase.from('organizations').select('preventive_window_days').is('deleted_at', null).limit(1).single();
    const w = (o.data as any)?.preventive_window_days ?? 30;
    setWindowDays(w);
    const horizon = new Date(); horizon.setDate(horizon.getDate() + w);
    const [d, g] = await Promise.all([
      supabase.from('asset_obligations')
        .select('id,due_date,status,assets(name),asset_obligation_rules(name_i18n,rule_code)')
        .is('deleted_at', null).in('status', ['scheduled', 'due_soon', 'overdue'])
        .lte('due_date', horizon.toISOString().slice(0, 10)).order('due_date').limit(100),
      supabase.from('work_orders')
        .select('id,wo_number,status,created_at,assets(name),properties(name)')
        .not('source_obligation_id', 'is', null).is('deleted_at', null).order('created_at', { ascending: false }).limit(100),
    ]);
    setDue((d.data as unknown as Due[]) ?? []);
    setWos((g.data as unknown as WO[]) ?? []);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function generate() {
    if (!orgId) return;
    setBusy(true); setCreated(null);
    const { data, error } = await supabase.rpc('generate_preventive_work_orders', { p_org: orgId });
    setBusy(false);
    if (!error) { setCreated((data as number) ?? 0); load(); }
  }

  const oblName = (o: Due) => o.asset_obligation_rules?.name_i18n?.[i18n.language] ?? o.asset_obligation_rules?.name_i18n?.en ?? o.asset_obligation_rules?.rule_code ?? '—';
  const openWos = useMemo(() => wos.filter((w) => ['open', 'acknowledged', 'assigned', 'in_progress'].includes(w.status)), [wos]);
  const today = useMemo(() => new Date().toLocaleDateString('en-CA', { timeZone: timezone }), [timezone]);
  const overdueDue = useMemo(() => due.filter((o) => o.due_date && o.due_date < today), [due, today]);
  const upcomingDue = useMemo(() => due.filter((o) => !o.due_date || o.due_date >= today), [due, today]);

  const oblTable = (rows: Due[]) => (
    <Card className="overflow-hidden p-0">
      <table className="w-full border-collapse text-sm">
        <thead><tr className="text-left text-xs text-muted">
          <th className="px-5 py-3 font-medium">{t('asset.obligation')}</th>
          <th className="px-2 py-3 font-medium">{t('nav.assets')}</th>
          <th className="px-2 py-3 font-medium">{t('asset.nextDue')}</th>
          <th className="px-5 py-3 font-medium">{t('asset.statusCol')}</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>}
          {rows.map((o) => {
            const sd = OBLIGATION_STATUS[o.status] ?? OBLIGATION_STATUS.scheduled!;
            return (
              <tr key={o.id} className="border-t border-divider">
                <td className="px-5 py-3 font-medium">{oblName(o)}</td>
                <td className="px-2 py-3 text-muted">{o.assets?.name ?? '—'}</td>
                <td className="px-2 py-3 font-mono text-muted">{o.due_date ? fmtDate(o.due_date, i18n.language, timezone) : '—'}</td>
                <td className="px-5 py-3"><StatusPill tone={sd.tone}>{t(sd.labelKey)}</StatusPill></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );

  return (
    <Layout header={<Topbar actions={<Button onClick={generate} disabled={busy}>{busy ? t('common.saving') : t('preventive.generate')}</Button>} />}>
      <div className="flex flex-col gap-[18px] px-7 py-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.preventive')}</h1>
          <span className="text-sm text-muted">{t('preventive.window', { count: windowDays })}</span>
        </div>
        {created != null && (
          <div className="rounded-md bg-st-done-bg px-3 py-2 text-sm text-st-done-fg">{t('preventive.generated', { count: created })}</div>
        )}

        <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-4">
          <Card className="flex flex-col gap-1 p-[18px]"><span className="text-xs text-muted">{t('preventive.overdue')}</span><span className={`font-mono text-[22px] font-medium${overdueDue.length ? ' text-st-overdue-fg' : ''}`}>{overdueDue.length}</span></Card>
          <Card className="flex flex-col gap-1 p-[18px]"><span className="text-xs text-muted">{t('preventive.dueInWindow')}</span><span className="font-mono text-[22px] font-medium">{upcomingDue.length}</span></Card>
          <Card className="flex flex-col gap-1 p-[18px]"><span className="text-xs text-muted">{t('preventive.openGenerated')}</span><span className="font-mono text-[22px] font-medium">{openWos.length}</span></Card>
          <Card className="flex flex-col gap-1 p-[18px]"><span className="text-xs text-muted">{t('preventive.totalGenerated')}</span><span className="font-mono text-[22px] font-medium">{wos.length}</span></Card>
        </section>

        <h2 className="eyebrow">{t('preventive.overdue')} <span className="font-mono text-muted">{overdueDue.length}</span></h2>
        {oblTable(overdueDue)}

        <h2 className="eyebrow">{t('preventive.upcomingInWindow')} <span className="font-mono text-muted">{upcomingDue.length}</span></h2>
        {oblTable(upcomingDue)}

        <h2 className="eyebrow">{t('preventive.generatedWos')}</h2>
        <Card className="overflow-hidden p-0">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {wos.length === 0 && <tr><td className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>}
              {wos.map((w) => {
                const sd = WORK_ORDER_STATUS[w.status];
                return (
                  <tr key={w.id} className="border-t border-divider first:border-0">
                    <td className="px-5 py-3 font-mono text-xs text-muted">{w.wo_number ?? '—'}</td>
                    <td className="px-2 py-3 font-medium">{w.assets?.name ?? '—'}</td>
                    <td className="px-2 py-3 text-muted">{w.properties?.name ?? '—'}</td>
                    <td className="px-2 py-3 font-mono text-xs text-muted">{fmtDate(w.created_at, i18n.language, timezone)}</td>
                    <td className="px-5 py-3 text-right">{sd && <StatusPill tone={sd.tone}>{t(sd.labelKey)}</StatusPill>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>
    </Layout>
  );
}
